import type { Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { StyleObj } from '../../@types';
import { SKIN } from '../../types/enums';

const CARD_MARGIN = 24;
const makeEmptyOutcomeGrid = (theme: Theme) => ({
  borderRight: `1px solid ${theme.palette.neutral[700]}`,
  cursor: 'not-allowed',
});

const BET365_STYLES: StyleObj = {
  container: {
    backgroundColor: {
      xs: 'neutral.700',
      md: 'neutral.600',
    },
    alignItems: 'stretch',
    borderRadius: {
      md: 1,
    },
    display: 'grid',
    gridTemplateRows: '1fr auto',
    minWidth: {
      xs: '100%',
      md: `calc(100% - ${CARD_MARGIN}px)`,
      lg: `calc(100% / 2 - ${CARD_MARGIN}px)`,
      xl: `calc(100% / 3 - ${CARD_MARGIN}px)`,
    },
    maxWidth: 400,
    overflow: 'hidden',
  },
  content: {
    position: 'relative',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: { xs: 'start', md: 'center' },
    justifyContent: 'center',
    p: {
      xs: 1,
      md: 3,
    },
    gap: {
      xs: 1,
      md: 1.5,
    },
    borderBottom: (theme) => `1px solid ${theme.palette.neutral[700]}`,
  },
  contentWithMobileStatistics: {
    pr: {
      xs: 4,
      md: 3,
    },
  },
  eventParticipants: {
    width: 1,
    flexDirection: { xs: 'column', md: 'row' },
    gap: { xs: 1, md: 2 },
    justifyContent: 'center',
    alignItems: { xs: 'start', md: 'center' },
  },
  eventMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    minWidth: 0,
    maxWidth: '100%',
  },
  eventMetaText: {
    color: {
      xs: '#FFFFFF80',
      md: 'neutral.200',
    },
    fontSize: { xs: 10, md: 12 },
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  participantContainer: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'start', md: 'center' },
    justifyContent: 'space-between',
    gap: 1,
    overflow: 'hidden',
    flex: 1,
  },
  participant: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flex: 1,
    minWidth: 0,
  },
  participantName: {
    flexShrink: 1,
    alignItems: 'center',
    gap: 1,
  },
  featuredStatsButton: {
    position: 'absolute',
    top: { xs: 8, md: 'auto' },
    right: { xs: 10, md: 'auto' },
    transform: 'none',
    zIndex: 1,
  },
  desktopFeaturedStatsButton: {
    display: { xs: 'none', md: 'flex' },
    alignItems: 'center',
    alignSelf: 'flex-start',
    justifyContent: 'center',
    minWidth: 18,
    flexShrink: 0,
  },
  mobileFeaturedStatsButton: {
    display: { xs: 'flex', md: 'none' },
  },
  featuredStatsButtonIcon: {
    color: 'neutral.200',
  },
  startDate: {
    color: {
      xs: '#FFFFFF60',
      md: 'neutral.200',
    },
    fontSize: { xs: 9, md: 12 },
    fontWeight: { xs: 400 },
    letterSpacing: '0.5px',
  },
  emptyOutcomeGrid: (theme) => ({
    ...makeEmptyOutcomeGrid(theme),
    backgroundColor: alpha(theme.palette.neutral[600], 0.5),
  }),
  emptyOutcomeGridWrapper: {
    cursor: 'not-allowed',
  },
  emptyOutcome: { opacity: 0.3, backgroundColor: 'transparent' },
};

const GOLA_STYLES: StyleObj = {
  ...BET365_STYLES,
  emptyOutcomeGrid: makeEmptyOutcomeGrid,
  container: {
    ...BET365_STYLES.container,
    minWidth: {
      xs: '220px',
      md: '220px',
      lg: '220px',
      xl: '220px',
    },
    borderRight: '1px solid',
    borderColor: 'primary.500',
  },
};

export const EVENT_SKIN_STYLES = {
  [SKIN.BET365]: BET365_STYLES,
  [SKIN.GOLA]: GOLA_STYLES,
};
